import { useEffect, useRef } from 'react';
import { targetLayerOrder, planMoves } from '../lib/layer-tree';

/**
 * Keeps the MapLibre draw order in line with the layer tree's `layerOrder`.
 *
 * react-map-gl creates each Layer on top of the stack when it mounts, so a layer toggled
 * back on (or added late) would land above everything. This hook reorders after the fact:
 * all layers that belong to a tree entry (by source id) become one contiguous block sorted
 * by the entry order, anchored where the lowest of them already sits; the basemap, Terra
 * Draw and the on-map overlays are left alone (lib/layer-tree.js targetLayerOrder).
 *
 * It re-applies on every `styledata` (basemap switch, layer mount/unmount, our own
 * moveLayer) and whenever the entries or the order change, but only moves when the order
 * actually differs, so it settles in one pass.
 */
export function applyDrawOrder(map, entriesInOrder) {
  if (!map?.getStyle) return 0;
  const style = map.getStyle();
  const current = (style?.layers || []).map((l) => ({ id: l.id, source: l.source }));
  const target = targetLayerOrder(current, entriesInOrder);
  const moves = planMoves(current.map((l) => l.id), target);
  moves.forEach(([id, before]) => map.moveLayer(id, before));
  return moves.length;
}

/**
 * Wires `applyDrawOrder` to a live map. `getMap()` is read again on every check, so a map
 * that mounts late or is recreated (dock layout switch, remount) is picked up and the
 * `styledata` listener moves with it. Scheduling uses a timer, not requestAnimationFrame:
 * rAF never fires in a hidden/background tab, which left the sync stuck there for good.
 * Returns { schedule, dispose }. `timers` is injectable for tests.
 */
export function createDrawOrderSync({ getMap, getSpec, pollMs = 500, timers = globalThis }) {
  let map = null;
  let pending = null;
  const apply = () => {
    pending = null;
    if (!map) return;
    const { entries, order } = getSpec();
    const byKey = new Map(entries.map((e) => [e.key, e]));
    const inOrder = order.map((k) => byKey.get(k)).filter(Boolean);
    try { applyDrawOrder(map, inOrder); } catch { /* style mid-reload: the next styledata retries */ }
  };
  const schedule = () => {
    if (pending !== null) return;
    pending = timers.setTimeout(apply, 0);
  };
  const bind = () => {
    let next = null;
    try { next = getMap() || null; } catch { next = null; }
    if (next === map) return;
    if (map) map.off('styledata', schedule);
    map = next;
    if (map) { map.on('styledata', schedule); schedule(); }
  };
  bind();
  const poll = timers.setInterval(bind, pollMs);
  return {
    schedule: () => { bind(); schedule(); },
    dispose: () => {
      timers.clearInterval(poll);
      if (pending !== null) timers.clearTimeout(pending);
      pending = null;
      if (map) map.off('styledata', schedule);
      map = null;
    },
  };
}

export function useDrawOrderSync({ mapRefHolder, entries, order }) {
  const specRef = useRef({ entries, order });
  const syncRef = useRef(null);
  specRef.current = { entries, order };

  useEffect(() => {
    const sync = createDrawOrderSync({
      getMap: () => mapRefHolder?.current?.current?.getMap?.(),
      getSpec: () => specRef.current,
    });
    syncRef.current = sync;
    return () => { sync.dispose(); syncRef.current = null; };
  }, [mapRefHolder]);

  useEffect(() => { syncRef.current?.schedule(); }, [entries, order]);
}
